import { useState, useMemo } from "react";
import { formatCompactINR, formatINR, formatNum, exportCSV } from "../../lib/format";
import { Table2, Download, Search } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

export default function CampaignsTable({ campaigns, services }) {
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("all");
  const [service, setService] = useState("all");
  const [status, setStatus] = useState("all");

  const rows = useMemo(() => {
    let r = campaigns || [];
    if (platform !== "all") r = r.filter((c) => c.platform === platform);
    if (service !== "all") r = r.filter((c) => c.service_category === service);
    if (status !== "all") r = r.filter((c) => c.status === status);
    if (search.trim())
      r = r.filter((c) => c.campaign_name.toLowerCase().includes(search.toLowerCase()));
    return r;
  }, [campaigns, platform, service, status, search]);

  const platformBadge = (p) =>
    p === "meta"
      ? "bg-[#38BDF8]/15 text-[#38BDF8]"
      : "bg-[#818CF8]/15 text-[#818CF8]";

  return (
    <div data-testid="campaigns-table" className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Table2 className="w-5 h-5 text-[#38BDF8]" />
          <h2 className="font-display text-lg font-semibold">Campaign Explorer</h2>
          <span className="text-xs text-slate-500">({rows.length})</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="campaign-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search campaigns"
              className="h-9 w-44 pl-8 bg-[#0F172A] border-white/10 text-white text-xs rounded-full focus-visible:ring-[#38BDF8]" />
          </div>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger data-testid="filter-platform" className="h-9 w-[110px] bg-[#0F172A] border-white/10 text-slate-200 rounded-full text-xs">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
              <SelectItem value="all">All Platforms</SelectItem>
              <SelectItem value="meta">Meta</SelectItem>
              <SelectItem value="google">Google</SelectItem>
            </SelectContent>
          </Select>
          <Select value={service} onValueChange={setService}>
            <SelectTrigger data-testid="filter-service" className="h-9 w-[150px] bg-[#0F172A] border-white/10 text-slate-200 rounded-full text-xs">
              <SelectValue placeholder="Service" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
              <SelectItem value="all">All Services</SelectItem>
              {(services || []).map((s) => (
                <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="h-9 w-[110px] bg-[#0F172A] border-white/10 text-slate-200 rounded-full text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="export-campaigns-csv" onClick={() => exportCSV(rows, "campaigns.csv")}
            className="h-9 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full text-xs">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
              <th className="text-left font-medium py-2.5 px-3">Campaign</th>
              <th className="text-left font-medium py-2.5 px-3">Platform</th>
              <th className="text-left font-medium py-2.5 px-3">Service</th>
              <th className="text-right font-medium py-2.5 px-3">Spend</th>
              <th className="text-right font-medium py-2.5 px-3">Clicks</th>
              <th className="text-right font-medium py-2.5 px-3">CTR</th>
              <th className="text-right font-medium py-2.5 px-3">CPC</th>
              <th className="text-right font-medium py-2.5 px-3">Leads</th>
              <th className="text-right font-medium py-2.5 px-3">CPL</th>
              <th className="text-center font-medium py-2.5 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={c.campaign_id}
                className={`border-b border-white/5 hover:bg-[#334155]/40 transition-colors ${i % 2 ? "bg-[#0F172A]/40" : ""}`}>
                <td className="py-2.5 px-3 text-slate-200 font-medium">{c.campaign_name}</td>
                <td className="py-2.5 px-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${platformBadge(c.platform)}`}>{c.platform}</span>
                </td>
                <td className="py-2.5 px-3 text-slate-400 text-xs">{c.service_category}</td>
                <td className="py-2.5 px-3 text-right tabular text-slate-200">{formatCompactINR(c.spend)}</td>
                <td className="py-2.5 px-3 text-right tabular text-slate-300">{formatNum(c.clicks)}</td>
                <td className="py-2.5 px-3 text-right tabular text-slate-300">{c.ctr}%</td>
                <td className="py-2.5 px-3 text-right tabular text-slate-300">{formatINR(c.cpc)}</td>
                <td className="py-2.5 px-3 text-right tabular text-slate-200">{formatNum(c.conversions)}</td>
                <td className="py-2.5 px-3 text-right tabular text-slate-200">{formatINR(c.cpl)}</td>
                <td className="py-2.5 px-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                    c.status === "active" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-slate-500/15 text-slate-400"}`}>
                    {c.status}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="py-8 text-center text-slate-500 text-sm">No campaigns match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
